// Smart Daara - Navbar Component
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Menu, X, User, LogOut, Settings, Globe, 
  LayoutDashboard, ChevronDown 
} from 'lucide-react';
import NotificationsDropdown from '../shared/NotificationsDropdown';
import { getDashboardPath } from '../../lib/routing';

// Smart Daara Logo URLs
const LOGO_URL = '/logo.png';
const FAVICON_URL = '/favicon.png';

export const Navbar = ({ onMenuClick }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Smooth-scroll to a section (navigate to /home first if needed)
  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    const doScroll = () => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (window.location.pathname !== '/home' && window.location.pathname !== '/') {
      navigate('/home');
      // Wait for landing page to mount
      setTimeout(doScroll, 250);
    } else {
      doScroll();
    }
  };

  const getDashboardPathLocal = () => getDashboardPath(user);

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/60" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left: hamburger (mobile, only when authenticated) + Logo */}
          <div className="flex items-center gap-2">
            {isAuthenticated && onMenuClick && (
              <button
                type="button"
                onClick={onMenuClick}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100"
                data-testid="sidebar-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>
            )}
            <Link to={isAuthenticated ? getDashboardPathLocal() : "/home"} className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="Smart Daara" className="h-8 sm:h-10 w-auto" />
              <span className="font-outfit font-bold text-lg sm:text-xl text-slate-900 hidden xs:inline">
                Smart Daara
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {!isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => scrollToSection('features')}
                  className="text-slate-600 hover:text-emerald-700 transition-colors"
                  data-testid="nav-features"
                >
                  {t('features')}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('pricing')}
                  className="text-slate-600 hover:text-emerald-700 transition-colors"
                  data-testid="nav-pricing"
                >
                  {t('pricing')}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className="text-slate-600 hover:text-emerald-700 transition-colors"
                  data-testid="nav-contact"
                >
                  {t('contact')}
                </button>
              </>
            ) : (
              <Link 
                to={getDashboardPathLocal()} 
                className="flex items-center gap-2 text-slate-600 hover:text-emerald-700 transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" />
                {t('dashboard')}
              </Link>
            )}

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-1"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium">{language.toUpperCase()}</span>
            </Button>

            {/* Auth Buttons */}
            {!isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="login-btn"
                >
                  {t('login')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {/* Notifications */}
                <NotificationsDropdown />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-700" />
                      </div>
                      <span className="font-medium text-slate-700">
                        {user.first_name}
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem 
                      onSelect={(e) => {
                        e.preventDefault();
                        navigate(getDashboardPathLocal());
                      }} 
                      data-testid="menu-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t('dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={(e) => {
                        e.preventDefault();
                        navigate(`${getDashboardPathLocal()}/profile`);
                      }} 
                      data-testid="menu-profile"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {t('profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={(e) => {
                        e.preventDefault();
                        navigate(`${getDashboardPathLocal()}/settings`);
                      }} 
                      data-testid="menu-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t('settings')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onSelect={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }} 
                      className="text-red-600" 
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-3">
              {!isAuthenticated ? (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 text-slate-600 text-left"
                    onClick={() => scrollToSection('features')}
                    data-testid="nav-features-mobile"
                  >
                    {t('features')}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-slate-600 text-left"
                    onClick={() => scrollToSection('pricing')}
                    data-testid="nav-pricing-mobile"
                  >
                    {t('pricing')}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-slate-600 text-left"
                    onClick={() => scrollToSection('contact')}
                    data-testid="nav-contact-mobile"
                  >
                    {t('contact')}
                  </button>
                  <div className="px-4 pt-3 border-t border-slate-200">
                    <Button className="bg-emerald-700 w-full" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                      {t('login')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link to={getDashboardPathLocal()} className="px-4 py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>
                    {t('dashboard')}
                  </Link>
                  <Link to={`${getDashboardPathLocal()}/profile`} className="px-4 py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-menu-profile">
                    {t('profile')}
                  </Link>
                  <Link to={`${getDashboardPathLocal()}/settings`} className="px-4 py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-menu-settings">
                    {t('settings')}
                  </Link>
                  <button
                    className="px-4 py-2 text-left text-red-600"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  >
                    {t('logout')}
                  </button>
                </>
              )}
              <button
                className="px-4 py-2 text-left text-slate-600 flex items-center gap-2"
                onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
              >
                <Globe className="w-4 h-4" />
                {language === 'fr' ? 'English' : 'Français'}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
